import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert, CircularProgress } from '@mui/material';
import Papa from 'papaparse';
import patientApi from '../api/patientApi';

const SAMPLE_CSV = `fullName,age,gender,phoneNumber,email,address,note,isActive\nJohn Doe,35,Male,9876543210,john@example.com,123 Main St,VIP,Active\nJane Smith,29,Female,9876543211,jane@example.com,456 Park Ave,,Inactive\n`;

const CSV_COLUMNS = [
  'fullName', 'age', 'gender', 'phoneNumber', 'email', 'address', 'note', 'isActive'
];

export interface PatientImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImportSuccess?: () => void;
}

export const PatientImportDialog: React.FC<PatientImportDialogProps> = ({ open, onClose, onImportSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<any[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setPreview([]);
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleParse = () => {
    if (!file) return;
    setParsing(true);
    setError(null);
    Papa.parse(file as any, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        setParsing(false);
        if (results.errors && results.errors.length > 0) {
          setError(results.errors.map((e: any) => e.message).join('; '));
          return;
        }
        const headers = results.meta.fields || [];
        const missing = CSV_COLUMNS.filter((h: string) => !headers.includes(h));
        if (missing.length > 0) {
          setError(`Missing required columns: ${missing.join(', ')}`);
          return;
        }
        setPreview(results.data as any[]);
      },
      error: () => setError('Failed to parse CSV'),
    });
  };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample-patients.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!preview.length) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      await patientApi.importPatients(preview);
      setImportResult(`Imported ${preview.length} patients successfully.`);
      if (onImportSuccess) onImportSuccess();
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setError(null);
    setParsing(false);
    setImporting(false);
    setImportResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Patients from CSV</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          Download the sample CSV, fill patient data, and upload to import. Required columns: <b>{CSV_COLUMNS.join(', ')}</b>.
        </Typography>
        <input
          accept=".csv"
          style={{ display: 'none' }}
          id="patient-import-file"
          type="file"
          onChange={handleFileChange}
          disabled={importing}
        />
        <label htmlFor="patient-import-file">
          <Button variant="outlined" color="primary" component="span">
            Choose CSV File
          </Button>
        </label>
        <Button sx={{ ml: 2 }} variant="text" color="secondary" onClick={handleDownloadSample}>
          Download Sample CSV
        </Button>
        <Button onClick={handleParse} disabled={!file || parsing} sx={{ mt: 2 }}>
          Parse CSV
        </Button>
        {parsing && <CircularProgress size={20} sx={{ ml: 2 }} />}
        {error && <Typography color="error" mt={2}>{error}</Typography>}
        {preview.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2">Preview ({preview.length} rows)</Typography>
            <Box sx={{ maxHeight: 120, overflow: 'auto', border: '1px solid #eee', p: 1, fontSize: 12 }}>
              <pre>{JSON.stringify(preview.slice(0, 5), null, 2)}{preview.length > 5 ? '\n...more' : ''}</pre>
            </Box>
          </Box>
        )}
        {importResult && <Typography color="success.main" mt={2}>{importResult}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>Cancel</Button>
        <Button onClick={handleImport} disabled={!preview.length || importing} variant="contained" color="primary">
          {importing ? 'Importing...' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientImportDialog;
